import * as THREE from 'three'
import Materials from './Materials.js'
import Floor from './Floor.js'
import Shadows from './Shadows.js'
import Physics from './Physics.js'
import Zones from './Zones.js'
import Objects from './Objects.js'
import Car from './Car.js'
import Areas from './Areas.js'
import Tiles from './Tiles.js'
import Walls from './Walls.js'
import IntroSection from './Sections/IntroSection.js'
import ProjectsSection from './Sections/ProjectsSection.js'
import CrossroadsSection from './Sections/CrossroadsSection.js'
import InformationSection from './Sections/InformationSection.js'
import PlaygroundSection from './Sections/PlaygroundSection.js'
// import DistinctionASection from './Sections/DistinctionASection.js'
// import DistinctionBSection from './Sections/DistinctionBSection.js'
// import DistinctionCSection from './Sections/DistinctionCSection.js'
// import DistinctionDSection from './Sections/DistinctionDSection.js'
import Controls from './Controls.js'
import Sounds from './Sounds.js'
import { TweenLite } from 'gsap/TweenLite'
import { Power2 } from 'gsap/EasePack'
import EasterEggs from './EasterEggs.js'

export default class
{
    constructor(_options)
    {
        // Options
        this.config = _options.config
        this.debug = _options.debug
        this.resources = _options.resources
        this.time = _options.time
        this.sizes = _options.sizes
        this.camera = _options.camera
        this.renderer = _options.renderer
        this.passes = _options.passes

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('world')
            this.debugFolder.open()
        }

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        // this.setAxes()
        this.setSounds()
        this.setControls()
        this.setFloor()
        this.setAreas()
        this.setStartingScreen()
    }

    start()
    {
        window.setTimeout(() =>
        {
            this.camera.pan.enable()
        }, 2000)

        this.setReveal()
        this.setMaterials()
        this.setShadows()
        this.setPhysics()
        this.setZones()
        this.setObjects()
        this.setCar()
        this.areas.car = this.car
        this.setTiles()
        this.setWalls()
        this.setSections()
        this.setEasterEggs()
    }

    setReveal()
    {
        this.reveal = {}
        this.reveal.matcapsProgress = 0
        this.reveal.floorShadowsProgress = 0
        this.reveal.previousMatcapsProgress = null
        this.reveal.previousFloorShadowsProgress = null

        // Go method
        this.reveal.go = () =>
        {
            TweenLite.fromTo(this.reveal, 3, { matcapsProgress: 0 }, { matcapsProgress: 1 })
            TweenLite.fromTo(this.reveal, 3, { floorShadowsProgress: 0 }, { floorShadowsProgress: 1, delay: 0.5 })
            TweenLite.fromTo(this.shadows, 3, { alpha: 0 }, { alpha: 0.5, delay: 0.5 })

            if(this.sections.intro)
            {
                TweenLite.fromTo(this.sections.intro.instructions.arrows.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.5 })
                if(this.sections.intro.otherInstructions)
                {
                    TweenLite.fromTo(this.sections.intro.otherInstructions.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.75 })
                }
            }

            // Car
            this.physics.car.chassis.body.sleep()
            this.physics.car.chassis.body.position.set(0, 0, 12)

            window.setTimeout(() =>
            {
                this.physics.car.chassis.body.wakeUp()
            }, 300)

            // Sound
            TweenLite.fromTo(this.sounds.engine.volume, 0.5, { master: 0 }, { master: 0.7, delay: 0.3, ease: Power2.easeIn })
            window.setTimeout(() =>
            {
                this.sounds.play('reveal')
            }, 400)

            // Controls
            if(this.controls.touch)
            {
                window.setTimeout(() =>
                {
                    this.controls.touch.reveal()
                }, 400)
            }
        }

        // Time tick
        this.time.on('tick',() =>
        {
            // Matcap progress changed
            if(this.reveal.matcapsProgress !== this.reveal.previousMatcapsProgress)
            {
                // Update each material
                for(const _materialKey in this.materials.shades.items)
                {
                    const material = this.materials.shades.items[_materialKey]
                    material.uniforms.uRevealProgress.value = this.reveal.matcapsProgress
                }

                // Save
                this.reveal.previousMatcapsProgress = this.reveal.matcapsProgress
            }

            // Matcap progress changed
            if(this.reveal.floorShadowsProgress !== this.reveal.previousFloorShadowsProgress)
            {
                // Update each floor shadow
                for(const _mesh of this.objects.floorShadows)
                {
                    _mesh.material.uniforms.uAlpha.value = this.reveal.floorShadowsProgress
                }

                // Save
                this.reveal.previousFloorShadowsProgress = this.reveal.floorShadowsProgress
            }
        })

        // Debug
        if(this.debug)
        {
            this.debugFolder.add(this.reveal, 'matcapsProgress').step(0.0001).min(0).max(1).name('matcapsProgress')
            this.debugFolder.add(this.reveal, 'floorShadowsProgress').step(0.0001).min(0).max(1).name('floorShadowsProgress')
            this.debugFolder.add(this.reveal, 'go').name('reveal')
        }
    }

    setStartingScreen()
    {
        this.startingScreen = {}

        // Area
        this.startingScreen.area = this.areas.add({
            position: new THREE.Vector2(0, 0),
            halfExtents: new THREE.Vector2(2.35, 1.5),
            hasKey: false,
            testCar: false,
            active: false
        })

        // Loading label
        this.startingScreen.loadingLabel = {}
        this.startingScreen.loadingLabel.geometry = new THREE.PlaneBufferGeometry(2.5, 2.5 / 4)
        this.startingScreen.loadingLabel.image = new Image()
        this.startingScreen.loadingLabel.image.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAABABAMAAAAHc7SNAAAAMFBMVEUAAAD///9ra2ucnJzR0dH09PQmJiaNjY24uLjp6end3d1CQkLFxcVYWFiqqqp9fX3nQ5qrAAAEVUlEQVRo3u3YT08TQRQA8JEtW6CATGnDdvljaTwYE2IBI/HGRrwSetGTsZh4MPFQYiQe229gE++WePFY9Oqh1cRzieEDYIgXLxjPJu5M33vbZQszW+fgoS+B7ewO836znRl2lg1jGMP4P2Okw0yFvaKsklr3I99Tvl3iPPelGbQhKqxB4eN6N/7gVcsvbEAz1F4RLn67zzl/v6/oLvejGBQ9LsNphio4UFjmEAsVJuOK/zkDtc6w+gyTcZ3LyP6IAzjBDA+pj6LkEgAjW4kANsMAC6vmOvqAMU5RgVOTskQACicCmCcA9AXjkT5gj1MswqlxWcoTgKJ6HuAQAD5guNoAu8QpMnBul1ONMGD2PCBbRgDAKYq6AEtmXvtdj3S6GhRyW1t1DvkAgM0ggG7mu1t3xWFHFzAqv3wYCi0mY1UCGgiQPU+1oWIY8LoXcAA3qeYfr+kClvHW14PJ5OfCAgHYNAoDAORBQIrDvHjqH5c0ANTbORzBacbAQgUC2IAKAzI9gCSHlWEMLmgBPJxMvyARpIICALDm4nkAbwIA71EZx5UOgO48JnLoOhQIAN9sOgKoBoAE5r0aB8ARcNhtFzrg0VQmwCp8CAMeAADGc44S5GMBsF1aCEU2LcAcAPDCvwFytBDehCaUgJxRAKeF8BNUUQJ43iiAUlqwFKoBrTCAHjiagwEgU0YM5IYWYD4KoIgPwIXQwUbVgCXzgLpIBJNeDciWTQNskVsq1ADX/6kYBdCTjse5owbMiX+IpgGWOCPSuWpA2vN/TAMm5QTYg5IC4FdbMA0YF5Nb5s2rAaLyhzBgektGZWDArrgqi0U1QHxf38OABDwUDgTAjGfyPlTVgJT/67FBACbqyGYaaoBctQwD2vI4DecVAPkgZRhQlxPQks2rAePGAbZsRlaa1QBYEQBUHRCAmaXD0QDYxgFWdye05R9cDQCrmQYkeBA6gGXTgNEeQF4DMG4S4MLjOUZRA5A0CcjADgmjqgGwSwSg9wK1GIBS74KTgTxv/EHoiaVQsTOS5RoCJuiZyosB8EIrHpyowFiYofO0i4wCjhCQwL0hq2sCaFNM22S4JXloLk0AuLDTBzCBAAt3xykeA7CHe/mDbgdTvQ9GswSAwdbqA0giYASHjQUJnhQKhQ6z/d8rDA4hAG2Dsk042ejubHMM2nV6AMf93pCkaRjhh0WsWuz+6aasl2FwiAImReEts1/CSaFfwFouAJxC4RW+I4oCThBQE1X2WbKkBFDkqYDtJ0SHaYKq3pJJwCECjjiFPoC1w+2P0gumurgeBjT6AhIIGKOelGIAngWlFnRnMZjMIYBb7gtIIsAuYU+8GICpEhYyZVgIZ2g9rYYAX1lfAKvjnxzjnWrHALDn9K1h2k2aoI1ewGd2AWAVAVMHcKdW4wDYje739pNufJXhkJohgLu9zy4CHCKAJYUge4ddCojGyPrp9kaHmYjUi9N7+2wYwxjGZfEXMKxGE0GkkfIAAAAASUVORK5CYII='
        //this.startingScreen.loadingLabel.image.src = 'data:image/jpeg;base64,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'
        this.startingScreen.loadingLabel.texture = new THREE.Texture(this.startingScreen.loadingLabel.image)
        this.startingScreen.loadingLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.loadingLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.loadingLabel.texture.needsUpdate = true
        this.startingScreen.loadingLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0x17225a, alphaMap: this.startingScreen.loadingLabel.texture })
        this.startingScreen.loadingLabel.mesh = new THREE.Mesh(this.startingScreen.loadingLabel.geometry, this.startingScreen.loadingLabel.material)
        this.startingScreen.loadingLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.loadingLabel.mesh)

        // Start label
        this.startingScreen.startLabel = {}
        this.startingScreen.startLabel.geometry = new THREE.PlaneBufferGeometry(4.6,4.5 / 1.5)
        this.startingScreen.startLabel.image = new Image()
        this.startingScreen.startLabel.image.src = 'data:image/jpeg;base64,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'
       //this.startingScreen.startLabel.image.src = 'https://res.cloudinary.com/relefcdn/image/upload/v1677778845/escapeLogo_itrxzq.jpg'
       this.startingScreen.startLabel.texture = new THREE.Texture(this.startingScreen.startLabel.image)
        this.startingScreen.startLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.startLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.startLabel.texture.needsUpdate = true
        this.startingScreen.startLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, map: this.startingScreen.startLabel.texture })
        this.startingScreen.startLabel.material.opacity = 0
        this.startingScreen.startLabel.mesh = new THREE.Mesh(this.startingScreen.startLabel.geometry, this.startingScreen.startLabel.material)
        this.startingScreen.startLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.startLabel.mesh)
        
        // Progress
        this.resources.on('progress', (_progress) =>
        {
            // Update area
            this.startingScreen.area.floorBorder.material.uniforms.uAlpha.value = 1
            this.startingScreen.area.floorBorder.material.uniforms.uColor.value = new THREE.Color(0x17225a)
            this.startingScreen.area.floorBorder.material.uniforms.uLoadProgress.value = _progress
        })

        // Ready
        this.resources.on('ready', () =>
        {
            window.requestAnimationFrame(() =>
            {
                this.startingScreen.area.activate()

                TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uAlpha, 0.3, { value: 0.3 })
                TweenLite.to(this.startingScreen.loadingLabel.material, 0.3, { opacity: 0 })
                TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 1, delay: 0.3 })
            })
        })

        // On interact, reveal
        this.startingScreen.area.on('interact', () =>
        {
            this.startingScreen.area.deactivate()
            TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uProgress, 0.3, { value: 0, delay: 0.4 })

            TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 0, delay: 0.4 })

           this.start()

            window.setTimeout(() =>
            {
                this.reveal.go()
            }, 600)
        })
    }

    setSounds()
    {
        this.sounds = new Sounds({
            debug: this.debugFolder,
            time: this.time
        })
    }

    setAxes()
    {
        this.axis = new THREE.AxesHelper()
        this.container.add(this.axis)
    }

    setControls()
    {
        this.controls = new Controls({
            config: this.config,
            sizes: this.sizes,
            time: this.time,
            camera: this.camera,
            sounds: this.sounds
        })
    }

    setMaterials()
    {
        this.materials = new Materials({
            resources: this.resources,
            debug: this.debugFolder
        })
    }

    setFloor()
    {
        this.floor = new Floor({
            debug: this.debugFolder
        })

        this.container.add(this.floor.container)
    }

    setShadows()
    {
        this.shadows = new Shadows({
            time: this.time,
            debug: this.debugFolder,
            renderer: this.renderer,
            camera: this.camera
        })
        this.container.add(this.shadows.container)
    }

    setPhysics()
    {
        this.physics = new Physics({
            config: this.config,
            debug: this.debug,
            time: this.time,
            sizes: this.sizes,
            controls: this.controls,
            sounds: this.sounds
        })

        this.container.add(this.physics.models.container)
    }

    setZones()
    {
        this.zones = new Zones({
            time: this.time,
            physics: this.physics,
            debug: this.debugFolder
        })
        this.container.add(this.zones.container)
    }

    setAreas()
    {
        this.areas = new Areas({
            config: this.config,
            resources: this.resources,
            debug: this.debug,
            renderer: this.renderer,
            camera: this.camera,
            car: this.car,
            sounds: this.sounds,
            time: this.time
        })

        this.container.add(this.areas.container)
    }

    setTiles()
    {
        this.tiles = new Tiles({
            resources: this.resources,
            objects: this.objects,
            debug: this.debug
        })
    }

    setWalls()
    {
        this.walls = new Walls({
            resources: this.resources,
            objects: this.objects
        })
    }

    setObjects()
    {
        this.objects = new Objects({
            time: this.time,
            resources: this.resources,
            materials: this.materials,
            physics: this.physics,
            shadows: this.shadows,
            sounds: this.sounds,
            debug: this.debugFolder
        })
        this.container.add(this.objects.container)

        // window.requestAnimationFrame(() =>
        // {
        //     this.objects.merge.update()
        // })
    }

    setCar()
    {
        this.car = new Car({
            time: this.time,
            resources: this.resources,
            objects: this.objects,
            physics: this.physics,
            shadows: this.shadows,
            materials: this.materials,
            controls: this.controls,
            sounds: this.sounds,
            renderer: this.renderer,
            camera: this.camera,
            debug: this.debugFolder,
            config: this.config
        })
        this.container.add(this.car.container)
    }

    setSections()
    {
        this.sections = {}

        // Generic options
        const options = {
            config: this.config,
            time: this.time,
            resources: this.resources,
            camera: this.camera,
            passes: this.passes,
            objects: this.objects,
            areas: this.areas,
            zones: this.zones,
            walls: this.walls,
            tiles: this.tiles,
            debug: this.debugFolder
        }

        // // Distinction A
        // this.sections.distinctionA = new DistinctionASection({
        //     ...options,
        //     x: 0,
        //     y: - 15
        // })
        // this.container.add(this.sections.distinctionA.container)

        // // Distinction B
        // this.sections.distinctionB = new DistinctionBSection({
        //     ...options,
        //     x: 0,
        //     y: - 15
        // })
        // this.container.add(this.sections.distinctionB.container)

        // // Distinction C
        // this.sections.distinctionC = new DistinctionCSection({
        //     ...options,
        //     x: 0,
        //     y: 0
        // })
        // this.container.add(this.sections.distinctionC.container)

        // // Distinction D
        // this.sections.distinctionD = new DistinctionDSection({
        //     ...options,
        //     x: 0,
        //     y: 0
        // })
        // this.container.add(this.sections.distinctionD.container)

        // Intro
        this.sections.intro = new IntroSection({
            ...options,
            x: 0,
            y: 0
        })
        this.container.add(this.sections.intro.container)

        // Crossroads
        this.sections.crossroads = new CrossroadsSection({
            ...options,
            x: 0,
            y: - 30
        })
        this.container.add(this.sections.crossroads.container)

        // Projects
        this.sections.projects = new ProjectsSection({
            ...options,
            x: 30,
            y: - 30
            // x: 0,
            // y: 0
        })
        this.container.add(this.sections.projects.container)

        // Information
        this.sections.information = new InformationSection({
            ...options,
            x: 1.2,
            y: - 55
            // x: 0,
            // y: - 10
        })
         this.container.add(this.sections.information.container)

        // Playground
        this.sections.playground = new PlaygroundSection({
            ...options,
            x: - 38,
            y: - 34
            // x: - 15,
            // y: - 4
        })
        this.container.add(this.sections.playground.container)
    }

    setEasterEggs()
    {
        this.easterEggs = new EasterEggs({
            resources: this.resources,
            car: this.car,
            walls: this.walls,
            objects: this.objects,
            materials: this.materials,
            areas: this.areas,
            config: this.config,
            physics: this.physics
        })
        this.container.add(this.easterEggs.container)
    }
}
